import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import './ReportProgressModal.scss';

export type ReportProgressItemStatus = 'pending' | 'processing' | 'done' | 'skipped' | 'error';

export type ReportProgressItem = {
    id: string;
    label: string;
    status: ReportProgressItemStatus;
    message?: string;
    detail?: string;
};

export type ReportProgressPhase = 'confirm' | 'running' | 'complete';

export type ReportProgressSummary = {
    total: number;
    succeeded: number;
    skipped: number;
    failed: number;
};

export type ReportProgressModalProps = {
    open: boolean;
    phase: ReportProgressPhase;
    title: string;
    subtitle: string;
    headerIcon: React.ReactNode;
    stepLabels: [string, string, string];
    alertContent: React.ReactNode;
    listSectionLabel: string;
    items: ReportProgressItem[];
    summary: ReportProgressSummary | null;
    confirmLabel: string;
    completeProgressLabel: string;
    startProgressLabel: string;
    runningProgressLabel: string;
    runningButtonLabel: string;
    successStatLabel: string;
    detailPrefix?: string;
    progressOverride?: string | null;
    onConfirm: () => void;
    onClose: () => void;
};

const STATUS_LABEL: Record<ReportProgressItemStatus, string> = {
    pending: 'Pending',
    processing: 'Running',
    done: 'Done',
    skipped: 'Skipped',
    error: 'Failed',
};

const statusIcon = (status: ReportProgressItemStatus) => {
    switch (status) {
        case 'done':
            return <CheckCircleOutlineIcon fontSize="small" color="success" />;
        case 'error':
            return <ErrorOutlineIcon fontSize="small" color="error" />;
        case 'skipped':
            return <SkipNextIcon fontSize="small" color="warning" />;
        case 'processing':
            return <SyncIcon fontSize="small" color="primary" className="report-progress-modal__spin" />;
        default:
            return <HourglassEmptyIcon fontSize="small" color="disabled" />;
    }
};

const PhaseSteps = ({
    phase,
    stepLabels,
}: {
    phase: ReportProgressPhase;
    stepLabels: [string, string, string];
}) => {
    const steps = [
        { key: 'confirm', label: stepLabels[0] },
        { key: 'running', label: stepLabels[1] },
        { key: 'complete', label: stepLabels[2] },
    ] as const;

    const phaseIndex = steps.findIndex((s) => s.key === phase);

    return (
        <div className="report-progress-modal__steps">
            {steps.map((step, index) => {
                const isActive = step.key === phase;
                const isDone = index < phaseIndex;
                return (
                    <span
                        key={step.key}
                        className={[
                            'report-progress-modal__step',
                            isActive ? 'report-progress-modal__step--active' : '',
                            isDone ? 'report-progress-modal__step--done' : '',
                        ].filter(Boolean).join(' ')}
                    >
                        <span className="report-progress-modal__step-dot" />
                        {step.label}
                    </span>
                );
            })}
        </div>
    );
};

const ReportProgressModal: React.FC<ReportProgressModalProps> = ({
    open,
    phase,
    title,
    subtitle,
    headerIcon,
    stepLabels,
    alertContent,
    listSectionLabel,
    items,
    summary,
    confirmLabel,
    completeProgressLabel,
    startProgressLabel,
    runningProgressLabel,
    runningButtonLabel,
    successStatLabel,
    detailPrefix = 'Detail',
    progressOverride,
    onConfirm,
    onClose,
}) => {
    const isRunning = phase === 'running';
    const isComplete = phase === 'complete';
    const completedCount = items.filter((item) => ['done', 'skipped', 'error'].includes(item.status)).length;
    const progressPct = items.length ? Math.round((completedCount / items.length) * 100) : 0;
    const currentItem = items.find((item) => item.status === 'processing');

    const handleDialogClose = () => {
        if (isRunning) {
            return;
        }
        onClose();
    };

    const progressLabel = progressOverride
        ? progressOverride
        : isComplete
          ? completeProgressLabel
          : currentItem
            ? `${runningProgressLabel} ${currentItem.label}…`
            : startProgressLabel;

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            maxWidth="sm"
            fullWidth
            className="report-progress-modal"
        >
            <div className="report-progress-modal__header">
                <div className="report-progress-modal__header-top">
                    <div className="report-progress-modal__header-icon">{headerIcon}</div>
                    <div>
                        <h2 className="report-progress-modal__title">{title}</h2>
                        <p className="report-progress-modal__subtitle">{subtitle}</p>
                    </div>
                </div>
                <PhaseSteps phase={phase} stepLabels={stepLabels} />
                {!isRunning && (
                    <IconButton
                        onClick={handleDialogClose}
                        className="report-progress-modal__close"
                        aria-label="Close"
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </div>

            <DialogContent className="report-progress-modal__content">
                {phase === 'confirm' && (
                    <>
                        <div className="report-progress-modal__alert">
                            <InfoOutlinedIcon className="report-progress-modal__alert-icon" fontSize="small" />
                            <div className="report-progress-modal__alert-text">{alertContent}</div>
                        </div>

                        <div className="report-progress-modal__section-label">
                            <span>{listSectionLabel}</span>
                            <span className="report-progress-modal__count-badge">{items.length}</span>
                        </div>

                        <div className="report-progress-modal__order-list">
                            {items.map((item, index) => (
                                <div key={item.id} className="report-progress-modal__order-chip">
                                    <span className="report-progress-modal__order-index">{index + 1}</span>
                                    <span className="report-progress-modal__order-label" title={item.label}>
                                        {item.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {(phase === 'running' || phase === 'complete') && (
                    <>
                        <div className="report-progress-modal__progress-card">
                            <div className="report-progress-modal__progress-meta">
                                <span className="report-progress-modal__progress-label">{progressLabel}</span>
                                <span className="report-progress-modal__progress-count">
                                    {completedCount} / {items.length} · {isComplete ? 100 : progressPct}%
                                </span>
                            </div>
                            <LinearProgress
                                variant="determinate"
                                value={isComplete ? 100 : progressPct}
                                className="report-progress-modal__progress-bar"
                            />
                        </div>

                        {isComplete && summary && (
                            <div className="report-progress-modal__summary-grid">
                                <div className="report-progress-modal__summary-stat report-progress-modal__summary-stat--success">
                                    <span className="report-progress-modal__summary-value">{summary.succeeded}</span>
                                    <span className="report-progress-modal__summary-key">{successStatLabel}</span>
                                </div>
                                {summary.skipped > 0 && (
                                    <div className="report-progress-modal__summary-stat report-progress-modal__summary-stat--warning">
                                        <span className="report-progress-modal__summary-value">{summary.skipped}</span>
                                        <span className="report-progress-modal__summary-key">Skipped</span>
                                    </div>
                                )}
                                {summary.failed > 0 && (
                                    <div className="report-progress-modal__summary-stat report-progress-modal__summary-stat--error">
                                        <span className="report-progress-modal__summary-value">{summary.failed}</span>
                                        <span className="report-progress-modal__summary-key">Failed</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="report-progress-modal__section-label">
                            <span>Live progress</span>
                        </div>

                        <div className="report-progress-modal__log">
                            {items.map((item) => (
                                <div
                                    key={item.id}
                                    className={[
                                        'report-progress-modal__log-row',
                                        `report-progress-modal__log-row--${item.status}`,
                                    ].join(' ')}
                                >
                                    <div className="report-progress-modal__log-icon">{statusIcon(item.status)}</div>
                                    <div className="report-progress-modal__log-body">
                                        <div className="report-progress-modal__log-title" title={item.label}>
                                            {item.label}
                                        </div>
                                        {item.message && (
                                            <div className="report-progress-modal__log-message">{item.message}</div>
                                        )}
                                        {item.detail && item.status === 'done' && (
                                            <span className="report-progress-modal__log-copy" title={item.detail}>
                                                {detailPrefix}: {item.detail}
                                            </span>
                                        )}
                                    </div>
                                    <span className="report-progress-modal__log-status">
                                        {STATUS_LABEL[item.status]}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </DialogContent>

            <DialogActions className="report-progress-modal__actions">
                {phase === 'confirm' && (
                    <>
                        <Button onClick={onClose} color="inherit" variant="outlined">
                            Cancel
                        </Button>
                        <Button
                            onClick={onConfirm}
                            variant="contained"
                            color="secondary"
                            autoFocus
                            startIcon={<PlayArrowRoundedIcon />}
                        >
                            {confirmLabel}
                        </Button>
                    </>
                )}
                {(phase === 'running' || phase === 'complete') && (
                    <Button onClick={onClose} variant="contained" color="secondary" disabled={isRunning} fullWidth>
                        {isComplete ? 'Close' : runningButtonLabel}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ReportProgressModal;
